import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ShoppingCart, UtensilsCrossed, Receipt, Truck, Ban, Soup } from "lucide-react";

import type { RootState, AppDispatch } from "../../../redux/store";
import { listenToFoodBrandAnalytics } from "../../../redux/foodOrderSlice";

import {
  HEART, InsightsStyles, StatTile, SectionHead, EmptyState, money, compact,
} from "./FreshInsightsKit";

interface FreshAnalyticsProps { brandId: string }

/* the food slice registers under "FoodOrder"; older stores wired it as
   "foodOrder" — read both so this doesn't depend on the store key */
const foodState = (s: RootState): any =>
  (s as any).FoodOrder ?? (s as any).foodOrder ?? {};

const FreshAnalytics: React.FC<FreshAnalyticsProps> = ({ brandId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const a = useSelector((s: RootState) => foodState(s).brandAnalytics ?? {});

  useEffect(() => {
    if (!brandId) return;
    const unsub = dispatch(listenToFoodBrandAnalytics(brandId) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [brandId, dispatch]);

  const top = (a.topProducts ?? []) as any[];
  const peak = top.length ? Math.max(...top.map((p) => p.revenue)) : 0;

  return (
    <div className="si">
      <InsightsStyles />

      <SectionHead title="Analytics" />

      <div className="si-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", marginBottom: 20 }}>
        <StatTile icon={<ShoppingCart size={15} />} label="Paid orders" value={compact(a.orders)} delay={0} />
        <StatTile icon={<UtensilsCrossed size={15} />} label="Dishes served" value={compact(a.units)} delay={1} />
        <StatTile icon={<Receipt size={15} />} label="Revenue" value={money(a.revenue)} tone="green" delay={2} />
        <StatTile icon={<Receipt size={15} />} label="Average order" value={money(a.avgOrderValue)} delay={3} />
      </div>

      {/* fulfilment — the honest stand-in for a conversion rate.
          Menu views aren't tracked anywhere, so there's nothing to
          divide by; what the order data does know is how much of
          what left the pass actually reached a doorstep. */}
      <div className="si-slab" style={{ padding: "16px 18px 18px", marginBottom: 20 }}>
        <p className="si-eyebrow">FULFILMENT</p>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <span className="si-num" style={{ fontSize: 30, color: HEART.dark }}>{a.fulfilmentRate ?? 0}%</span>
          <span style={{ fontSize: 12, color: HEART.textMuted, fontWeight: 600 }}>
            of paid orders delivered
          </span>
        </div>
        <div className="si-track" style={{ marginBottom: 12 }}>
          <div className="si-fill gn" style={{ width: `${Math.min(100, a.fulfilmentRate ?? 0)}%` }} />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <span className="si-pill" style={{ background: HEART.greenSoft, color: HEART.green }}>
            <Truck size={10} /> {compact(a.deliveredOrders)} delivered
          </span>
          <span className="si-pill" style={{ background: HEART.redSoft, color: HEART.redDark }}>
            <Ban size={10} /> {compact(a.cancelledOrders)} cancelled
          </span>
        </div>
      </div>

      {top.length === 0 ? (
        <EmptyState
          emoji="🍳"
          title="Nothing served yet"
          body="Once orders come through the pass, your best dishes will rank here by revenue."
        />
      ) : (
        <div className="si-slab">
          <div className="si-strip" />
          <div style={{ padding: "14px 16px 4px" }}>
            <p className="si-eyebrow">TOP DISHES BY REVENUE</p>
          </div>

          {top.map((p, i) => (
            <div key={p.listingId} className="si-row" style={{ alignItems: "flex-start" }}>
              <span className="si-rank" style={{ marginTop: 2 }}>{i + 1}</span>

              <div style={{
                width: 44, height: 44, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                border: `2.5px solid ${HEART.dark}`, background: HEART.blush,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {p.photo
                  ? <img src={p.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <Soup size={16} color={HEART.textFaint} />}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div title={p.name} style={{
                  fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 13,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: -.2,
                }}>
                  {p.name}
                </div>
                <div style={{ fontSize: 10.5, color: HEART.textMuted, fontWeight: 600, margin: "3px 0 7px" }}>
                  {p.units} served across {p.orders} order{p.orders > 1 ? "s" : ""}
                </div>
                <div className="si-track" style={{ height: 9 }}>
                  <div className="si-fill" style={{ width: peak ? `${(p.revenue / peak) * 100}%` : "0%" }} />
                </div>
              </div>

              <div className="si-num" style={{ fontSize: 14, color: HEART.green, flexShrink: 0, marginTop: 2 }}>
                {money(p.revenue)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FreshAnalytics;
